import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, EyeSlash, Trash, Star, PushPin } from "@phosphor-icons/react";
import { api, errorMessage, fmtDate } from "@/lib/api";
import { AdminCard, adminBtn, adminBtnGhost, adminInput } from "./AdminLayout";

const TABS = [
  { key: "pending", label: "Awaiting approval" },
  { key: "approved", label: "Live on site" },
  { key: "hidden", label: "Hidden" },
  { key: "", label: "All" },
];

export default function AdminReviews() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("pending");
  const [praise, setPraise] = useState({ name: "", city: "", body: "", rating: "", featured: false });
  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ["admin-reviews", tab],
    queryFn: async () => (await api.get("/admin/reviews", { params: tab ? { status: tab } : {} })).data,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-reviews"] }); qc.invalidateQueries({ queryKey: ["reviews"] }); qc.invalidateQueries({ queryKey: ["books"] }); };

  const patch = useMutation({
    mutationFn: ({ review_id, ...body }) => api.put(`/admin/reviews/${review_id}`, body),
    onSuccess: () => { refresh(); toast.success("Review updated"); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const addPraise = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/reviews/curated", { ...praise, rating: praise.rating ? Number(praise.rating) : null });
      setPraise({ name: "", city: "", body: "", rating: "", featured: false });
      refresh();
      toast.success("Praise added and live on the site");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the review by ${r.name}?`)) return;
    try { await api.delete(`/admin/reviews/${r.review_id}`); refresh(); toast.success("Review deleted"); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-reviews-page">
      <div>
        <h1 className="text-2xl font-medium tracking-tight">Reviews</h1>
        <p className="mt-1 text-sm text-muted-foreground">Buyers get a one-tap review link in their delivery email. Nothing shows on the site until you approve it.</p>
      </div>

      <AdminCard title="Add praise you've received">
        <form onSubmit={addPraise} className="grid items-end gap-3 sm:grid-cols-6" data-testid="praise-form">
          <label className="text-xs sm:col-span-1"><span className="mb-1 block text-muted-foreground">Name</span><input required value={praise.name} onChange={(e) => setPraise({ ...praise, name: e.target.value })} className={adminInput} data-testid="praise-name-input" /></label>
          <label className="text-xs sm:col-span-1"><span className="mb-1 block text-muted-foreground">City / title</span><input value={praise.city} onChange={(e) => setPraise({ ...praise, city: e.target.value })} className={adminInput} data-testid="praise-city-input" /></label>
          <label className="text-xs sm:col-span-3"><span className="mb-1 block text-muted-foreground">What they said</span><input required value={praise.body} onChange={(e) => setPraise({ ...praise, body: e.target.value })} className={adminInput} data-testid="praise-body-input" /></label>
          <label className="text-xs"><span className="mb-1 block text-muted-foreground">Stars (optional)</span>
            <select value={praise.rating} onChange={(e) => setPraise({ ...praise, rating: e.target.value })} className={adminInput} data-testid="praise-rating-select">
              <option value="">None</option>{[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <div className="flex items-center gap-3 sm:col-span-6">
            <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={praise.featured} onChange={(e) => setPraise({ ...praise, featured: e.target.checked })} data-testid="praise-featured-toggle" /> Pin to the top of the wall</label>
            <button type="submit" className={adminBtn} data-testid="praise-add-button">Add praise</button>
          </div>
        </form>
      </AdminCard>

      <div className="flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${tab === t.key ? "bg-foreground text-white" : "bg-muted hover:bg-secondary"}`} data-testid={`reviews-tab-${t.key || "all"}`}>{t.label}</button>
        ))}
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : reviews.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="reviews-empty">Nothing here yet.</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {reviews.map((r) => (
            <AdminCard key={r.review_id}>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{r.name}</span>
                {r.city && <span>· {r.city}</span>}
                {r.rating && <span className="flex items-center gap-0.5 text-primary"><Star size={12} weight="fill" /> {r.rating}</span>}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.status === "approved" ? "bg-[#E7EFE4] text-[#3F6B3A]" : r.status === "pending" ? "bg-[#FBEFD8] text-[#8A5A17]" : "bg-muted"}`}>{r.status}</span>
                {r.verified && <span className="rounded-full bg-[#F6E7DF] px-2 py-0.5 text-[10px] font-semibold text-[#9A4426]">verified buyer</span>}
                {r.source === "curated" && <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold">praise</span>}
                {r.featured && <span className="flex items-center gap-0.5 text-[10px] font-semibold text-primary"><PushPin size={11} weight="fill" /> pinned</span>}
                <span className="ml-auto">{fmtDate(r.created_at)}</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed">{r.body}</p>
              {r.favourite_poem_ta && <p className="mt-2 text-xs text-primary">Favourite poem: {r.favourite_poem_ta}</p>}
              <textarea rows={2} defaultValue={r.reply} placeholder="Public reply (optional)" onBlur={(e) => e.target.value !== (r.reply || "") && patch.mutate({ review_id: r.review_id, reply: e.target.value })}
                className={`${adminInput} mt-3 h-auto py-2`} data-testid={`review-reply-${r.review_id}`} />
              <div className="mt-3 flex flex-wrap gap-2">
                {r.status !== "approved" && <button onClick={() => patch.mutate({ review_id: r.review_id, status: "approved" })} className={adminBtn} data-testid={`review-approve-${r.review_id}`}><Check size={14} weight="bold" /> Approve</button>}
                {r.status !== "hidden" && <button onClick={() => patch.mutate({ review_id: r.review_id, status: "hidden" })} className={adminBtnGhost} data-testid={`review-hide-${r.review_id}`}><EyeSlash size={14} /> Hide</button>}
                <button onClick={() => patch.mutate({ review_id: r.review_id, featured: !r.featured })} className={adminBtnGhost} data-testid={`review-feature-${r.review_id}`}><PushPin size={14} /> {r.featured ? "Unpin" : "Pin"}</button>
                <button onClick={() => remove(r)} className={`${adminBtnGhost} text-destructive`} data-testid={`review-delete-${r.review_id}`}><Trash size={14} /></button>
              </div>
            </AdminCard>
          ))}
        </div>
      )}
    </div>
  );
}
